import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '@/components/Button';
import NotificationBadge from '@/components/NotificationBadge';
import NotificationBox from '@/components/NotificationBox';
import notificationIcon from '@/assets/notification.svg';
import { 
  getUnreadNotificationCount, 
  getNotifications, 
  markNotificationAsRead,
  markAllNotificationsAsRead 
} from '@/api/notifications';

/**
 * Notification dropdown component with button, badge, and notification box
 */
const NotificationDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [cursorState, setCursorState] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const prevIsOpenRef = useRef(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Query for unread count
  const { data: unreadCountData } = useQuery({
    queryKey: ['notificationUnreadCount'],
    queryFn: getUnreadNotificationCount,
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000, // Consider data stale after 10 seconds
  });

  // Query for notifications (only when dropdown is open)
  const { data: notificationsData, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['notifications', 'dropdown', cursor],
    queryFn: () => getNotifications({ 
      limit: 10, 
      unreadOnly: false,
      cursor 
    }),
    enabled: isOpen,
    staleTime: 0, // Always consider data stale, refetch when needed
    refetchOnMount: 'always', // Always refetch when component mounts/opens
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Update notifications when new data arrives
  useEffect(() => {
    // Only update when dropdown is open
    if (!isOpen) return;

    if (notificationsData?.data && Array.isArray(notificationsData.data)) {
      const isLoadingMore = cursor !== null;
      
      setNotifications(prev => {
        if (isLoadingMore) {
          // Append new notifications for pagination
          return [...prev, ...notificationsData.data];
        } else {
          // Replace notifications for initial load
          return notificationsData.data;
        }
      });

      // Update cursor state
      setCursorState(notificationsData.cursor || null);
    } else if (notificationsData && !notificationsData.data) {
      // Handle case where API returns success but no data property
      if (cursor === null) {
        setNotifications([]);
      }
      setCursorState(null);
    }
  }, [notificationsData, cursor, isOpen]);

  // Reset notifications and cursor when dropdown closes
  // Force refetch when dropdown opens
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (!isOpen) {
      // Close: reset everything
      setNotifications([]);
      setCursor(null);
      setCursorState(null);
    } else if (!wasOpen) {
      // Just opened: invalidate cache to ensure fresh data
      queryClient.invalidateQueries({ 
        queryKey: ['notifications', 'dropdown'],
        refetchType: 'active'
      });
    }
  }, [isOpen, queryClient]);

  // Mutation for marking notification as read
  const markAsReadMutation = useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      // Invalidate and refetch notification queries
      queryClient.invalidateQueries({ queryKey: ['notificationUnreadCount'] });
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
        refetchType: 'active' // Only refetch active queries
      });
    },
  });

  // Mutation for marking all notifications as read
  const markAllAsReadMutation = useMutation({
    mutationFn: markAllNotificationsAsRead,
    onSuccess: () => {
      // Invalidate and refetch notification queries
      queryClient.invalidateQueries({ queryKey: ['notificationUnreadCount'] });
      queryClient.invalidateQueries({
        queryKey: ['notifications'],
        refetchType: 'active' // Only refetch active queries
      });
    },
  });

  const unreadCount = unreadCountData?.data?.count || 0;
  const hasMoreNotifications = Boolean(cursorState);

  // Handle button click
  const handleButtonClick = () => {
    setIsOpen(!isOpen);
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    // Mark as read if not already read
    if (!notification.isRead) {
      markAsReadMutation.mutate(notification.id);
    }

    // Close dropdown
    setIsOpen(false);
  };

  // Handle view all click
  const handleViewAll = () => {
    // Navigate to notifications page
    navigate('/me/notifications');
    setIsOpen(false);
  };

  // Handle load more
  const handleLoadMore = () => {
    if (cursorState && !isFetching) {
      setCursor(cursorState);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative">
      <Button 
        ref={buttonRef}
        className="w-9 h-9 !px-0 relative"
        onClick={handleButtonClick}
      >
        <img src={notificationIcon} alt="Notifications" className="w-4 h-4" />
        <NotificationBadge count={unreadCount} />
      </Button>

      {isOpen && (
        <div 
          ref={dropdownRef}
          className="absolute top-[44px] right-0 z-50 w-[370px]"
        >
          <NotificationBox 
            notifications={notifications}
            onViewAll={handleViewAll}
            onNotificationClick={handleNotificationClick}
            onLoadMore={handleLoadMore}
            hasMore={hasMoreNotifications}
            isLoading={(isLoading || isFetching) && cursor === null}
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
